"use client";

// Executive scorecard — GET /api/admin/analytics/scorecard.
// The API publishes actuals only; KPI targets are not part of any endpoint
// yet, so this shows measured values rather than invented target columns.

import { adminApi, type ScorecardAnalytics } from "../lib/api";
import { useApi } from "../lib/useApi";
import { compactMoney, pct } from "../lib/format";
import { Skeleton } from "./Skeleton";
import { PanelError } from "./PanelState";

const border = "color-mix(in srgb, var(--neu-dark) 25%, transparent)";

export default function ExecutiveScorecard() {
  const { data, loading, error, reload } = useApi<ScorecardAnalytics>(
    (s) => adminApi.scorecard({}, s),
    [],
    "Failed to load the scorecard.",
  );

  const rows = data
    ? [
        { kpi: "Customers logged", value: String(data.summary.totalCustomers) },
        { kpi: "Deals", value: String(data.summary.totalDeals) },
        { kpi: "Active / pending", value: `${data.summary.activeDeals} / ${data.summary.pendingDeals}` },
        { kpi: "Sold / lost", value: `${data.summary.soldDeals} / ${data.summary.lostDeals}` },
        { kpi: "Close rate", value: pct(data.summary.closeRate) },
        { kpi: "Bell volume", value: String(data.summary.bellVolume) },
        { kpi: "Escalations", value: String(data.summary.escalations) },
        { kpi: "Approved worksheets", value: String(data.summary.approvedWorksheets) },
        { kpi: "Recorded gross", value: compactMoney(data.summary.recordedTotalGross) },
      ]
    : [];

  return (
    <div className="neu p-5 sm:p-6 flex flex-col h-full">
      <div className="mb-4">
        <h2 className="font-display text-lg text-text">Executive Scorecard</h2>
        <p className="text-[12.5px] text-text-faint">Store actuals · lifetime scope</p>
      </div>

      {loading ? (
        // Keep the KPI / Actual column headers: they are static labels, not
        // data, so hiding them makes the panel reflow when rows arrive.
        <div className="grid grid-cols-[1fr_auto] items-center gap-x-6">
          <span className="text-[11px] uppercase tracking-wide text-text-faint pb-2">KPI</span>
          <span className="text-[11px] uppercase tracking-wide text-text-faint pb-2 text-right">
            Actual
          </span>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="contents">
              <span className="py-3 border-t" style={{ borderColor: border }}>
                <Skeleton className="h-3.5 w-36 rounded" />
              </span>
              <span className="py-3 border-t flex justify-end" style={{ borderColor: border }}>
                <Skeleton className="h-3.5 w-14 rounded" />
              </span>
            </div>
          ))}
        </div>
      ) : error || !data ? (
        <PanelError message={error ?? "Scorecard unavailable."} onRetry={reload} />
      ) : (
        <div className="grid grid-cols-[1fr_auto] items-center gap-x-6 text-[13px]">
          <span className="text-[11px] uppercase tracking-wide text-text-faint pb-2">KPI</span>
          <span className="text-[11px] uppercase tracking-wide text-text-faint pb-2 text-right">Actual</span>

          {rows.map((row) => (
            <div key={row.kpi} className="contents">
              <span className="py-3 font-medium text-text border-t" style={{ borderColor: border }}>
                {row.kpi}
              </span>
              <span
                className="py-3 text-right font-semibold text-text tabular-nums border-t"
                style={{ borderColor: border }}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
